import React, { useState } from 'react';
import { Send, CheckCircle, Upload, X, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { businessInfo } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tilefix-scheduler/artifacts/c05hbcds_IMG_1809.png";

const EstimatePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    project_address: '',
    preferred_contact: [],
    project_types: [],
    project_type_other: '',
    project_size: '',
    known_square_footage: '',
    tile_selected: '',
    tile_types: [],
    tile_sizes: [],
    installation_type: '',
    surface_condition: '',
    additional_conditions: [],
    timeline: '',
    space_in_use: '',
    budget: '',
    additional_notes: ''
  });
  
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const currentValues = prev[field] || [];
      if (currentValues.includes(value)) {
        return { ...prev, [field]: currentValues.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...currentValues, value] };
      }
    });
  };

  const handleRadioChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploadingPhotos(true);
    
    for (const file of files) {
      try {
        // Create form data for upload
        const uploadData = new FormData();
        uploadData.append('file', file);
        
        // Upload to backend
        const response = await axios.post(`${API}/upload-photo`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Add to uploaded photos with server response
        setUploadedPhotos(prev => [...prev, {
          id: response.data.id,
          name: response.data.filename,
          url: `${API}/photos/${response.data.id}`,
          size: response.data.size,
          uploaded: true
        }]);
      } catch (err) {
        console.error('Error uploading photo:', err);
        // Still show the photo locally even if upload fails
        setUploadedPhotos(prev => [...prev, {
          name: file.name,
          url: URL.createObjectURL(file),
          uploaded: false,
          error: 'Upload failed'
        }]);
      }
    }
    
    setUploadingPhotos(false);
  };

  const removePhoto = (index) => {
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Prepare photos array with uploaded photo IDs
      const photoData = uploadedPhotos
        .filter(p => p.uploaded && p.id)
        .map(p => ({
          id: p.id,
          filename: p.name
        }));
      
      // Submit estimate with photo references
      await axios.post(`${API}/estimates`, {
        ...formData,
        photos: photoData
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting estimate:', err);
      setError('Failed to submit estimate. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  const CheckboxGroup = ({ label, field, options }) => (
    <div className="space-y-2">
      <label className="block text-white font-medium mb-3">{label}</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map(option => (
          <label key={option} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-[#152a42] border border-[#F7931E]/20 hover:border-[#F7931E]/50 transition-colors">
            <input
              type="checkbox"
              checked={formData[field]?.includes(option)}
              onChange={() => handleCheckboxChange(field, option)}
              className="w-5 h-5 rounded border-[#F7931E]/50 bg-[#0d1f33] text-[#F7931E] focus:ring-[#F7931E] focus:ring-offset-0"
            />
            <span className="text-[#c5d3e3]">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  const RadioGroup = ({ label, field, options }) => (
    <div className="space-y-2">
      <label className="block text-white font-medium mb-3">{label}</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map(option => (
          <label key={option} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg bg-[#152a42] border border-[#F7931E]/20 hover:border-[#F7931E]/50 transition-colors">
            <input
              type="radio"
              name={field}
              checked={formData[field] === option}
              onChange={() => handleRadioChange(field, option)}
              className="w-5 h-5 border-[#F7931E]/50 bg-[#0d1f33] text-[#F7931E] focus:ring-[#F7931E] focus:ring-offset-0"
            />
            <span className="text-[#c5d3e3]">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0d1f33] pt-24">
        <div className="container py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#F7931E] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#0d1f33]" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Thank You!</h2>
            <p className="text-xl text-[#c5d3e3] mb-4">
              Your estimate request has been submitted successfully.
            </p>
            <p className="text-[#8fa3b8] mb-8">
              We'll review your request and contact you within 24–48 hours to discuss next steps or schedule an on-site estimate.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" className="btn-primary">
                Return Home
              </Link>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    full_name: '', phone: '', email: '', project_address: '', preferred_contact: [],
                    project_types: [], project_type_other: '', project_size: '', known_square_footage: '',
                    tile_selected: '', tile_types: [], tile_sizes: [], installation_type: '',
                    surface_condition: '', additional_conditions: [], timeline: '', space_in_use: '',
                    budget: '', additional_notes: ''
                  });
                  setUploadedPhotos([]);
                }}
                className="btn-secondary"
              >
                Submit Another Request
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1f33]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0d1f33]/95 backdrop-blur-sm border-b border-[#F7931E]/20">
        <div className="container">
          <nav className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Top Notch Tile LLC" className="h-14 w-auto" />
            </Link>
            <Link to="/" className="flex items-center gap-2 text-[#8fa3b8] hover:text-white transition-colors">
              <ArrowLeft size={18} />
              <span>Back to Home</span>
            </Link>
          </nav>
        </div>
      </header>

      {/* Form Section */}
      <div className="pt-24 pb-16">
        <div className="container">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Request Your Free Estimate</h1>
            <p className="text-xl text-[#c5d3e3] max-w-2xl mx-auto">
              Fill out the form below and we'll review your request and contact you within 24–48 hours.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {error && (
              <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-200">
                {error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-[#152a42] p-6 md:p-8 rounded-lg border border-[#F7931E]/30">
              <div className="space-y-10">
                
                {/* 1. Contact Information */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    1. Contact Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white font-medium mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Smith"
                        className="form-input rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-medium mb-2">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        placeholder="(508) 555-1234"
                        className="form-input rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-medium mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@example.com"
                        className="form-input rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-medium mb-2">Project Address / Town / ZIP</label>
                      <input
                        type="text"
                        name="project_address"
                        value={formData.project_address}
                        onChange={handleInputChange}
                        placeholder="Osterville, MA 02655"
                        className="form-input rounded-lg"
                      />
                    </div>
                  </div>
                  <CheckboxGroup
                    label="Preferred Contact Method"
                    field="preferred_contact"
                    options={['Call', 'Text', 'Email']}
                  />
                </div>

                {/* 2. Project Type */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    2. Project Type
                  </h3>
                  <CheckboxGroup
                    label="What type of project is this? (Select all that apply)"
                    field="project_types"
                    options={[
                      'Bathroom floor',
                      'Shower walls',
                      'Shower floor',
                      'Kitchen backsplash',
                      'Kitchen floor',
                      'Entryway / Mudroom',
                      'Fireplace surround',
                      'Other'
                    ]}
                  />
                  {formData.project_types.includes('Other') && (
                    <div>
                      <label className="block text-white font-medium mb-2">Please describe:</label>
                      <input
                        type="text"
                        name="project_type_other"
                        value={formData.project_type_other}
                        onChange={handleInputChange}
                        placeholder="Describe your project..."
                        className="form-input rounded-lg"
                      />
                    </div>
                  )}
                </div>

                {/* 3. Project Size */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    3. Project Size
                  </h3>
                  <RadioGroup
                    label="Approximate area to be tiled:"
                    field="project_size"
                    options={[
                      'Small (under 25 sq ft)',
                      'Medium (25–75 sq ft)',
                      'Large (75–150 sq ft)',
                      'Extra Large (150+ sq ft)',
                      'Not sure'
                    ]}
                  />
                  <div>
                    <label className="block text-white font-medium mb-2">If known, approximate square footage (optional):</label>
                    <input
                      type="text"
                      name="known_square_footage"
                      value={formData.known_square_footage}
                      onChange={handleInputChange}
                      placeholder="e.g., 50"
                      className="form-input rounded-lg w-full md:w-48"
                    />
                  </div>
                </div>

                {/* 4. Tile Information */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    4. Tile Information
                  </h3>
                  <RadioGroup
                    label="Do you already have tile selected?"
                    field="tile_selected"
                    options={['Yes', 'No', 'Need recommendations']}
                  />
                  <CheckboxGroup
                    label="Tile type (if known):"
                    field="tile_types"
                    options={['Ceramic', 'Porcelain', 'Natural stone', 'Glass', 'Mosaic', 'Large format (12x24 or larger)']}
                  />
                  <CheckboxGroup
                    label="Tile size (optional):"
                    field="tile_sizes"
                    options={['Mosaic / small', 'Standard (12x12)', 'Large format']}
                  />
                </div>

                {/* 5. Surface & Prep Work */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    5. Surface & Prep Work
                  </h3>
                  <RadioGroup
                    label="Is this a new installation or replacement?"
                    field="installation_type"
                    options={['New install', 'Replacing existing tile']}
                  />
                  <RadioGroup
                    label="Existing surface condition:"
                    field="surface_condition"
                    options={['Clean & ready', 'Tile needs removal', 'Unsure']}
                  />
                  <CheckboxGroup
                    label="Any of the following?"
                    field="additional_conditions"
                    options={['Uneven floors', 'Water damage', 'Subfloor repair needed', 'Not sure']}
                  />
                </div>

                {/* 6. Timeline & Access */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    6. Timeline & Access
                  </h3>
                  <RadioGroup
                    label="Desired start timeframe:"
                    field="timeline"
                    options={['ASAP', 'Within 1–2 weeks', 'Within 1 month', 'Flexible']}
                  />
                  <RadioGroup
                    label="Is the space currently in use?"
                    field="space_in_use"
                    options={['Yes', 'No']}
                  />
                </div>

                {/* 7. Budget Range */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    7. Budget Range (Optional)
                  </h3>
                  <RadioGroup
                    label="Do you have a budget range in mind?"
                    field="budget"
                    options={['Under $1,000', '$1,000–$3,000', '$3,000–$5,000', '$5,000+', 'Not sure']}
                  />
                </div>

                {/* 8. Photos Upload */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    8. Photos (Optional but Recommended)
                  </h3>
                  <p className="text-[#8fa3b8] text-sm mb-4">
                    Upload photos of the current space, close-ups of corners/drains, or inspiration photos.
                  </p>
                  <div className="border-2 border-dashed border-[#F7931E]/30 rounded-lg p-6 text-center hover:border-[#F7931E]/60 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                      disabled={uploadingPhotos}
                    />
                    <label htmlFor="photo-upload" className={`cursor-pointer ${uploadingPhotos ? 'opacity-50' : ''}`}>
                      {uploadingPhotos ? (
                        <>
                          <div className="w-10 h-10 border-4 border-[#F7931E] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                          <p className="text-white font-medium">Uploading photos...</p>
                        </>
                      ) : (
                        <>
                          <Upload size={40} className="mx-auto text-[#F7931E] mb-3" />
                          <p className="text-white font-medium">Click to upload photos</p>
                          <p className="text-[#8fa3b8] text-sm">PNG, JPG up to 10MB each</p>
                        </>
                      )}
                    </label>
                  </div>
                  {uploadedPhotos.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                      {uploadedPhotos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photo.url}
                            alt={photo.name}
                            className={`w-full h-24 object-cover rounded-lg ${!photo.uploaded ? 'opacity-50' : ''}`}
                          />
                          {photo.uploaded ? (
                            <div className="absolute top-1 left-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                              <CheckCircle size={12} className="text-white" />
                            </div>
                          ) : (
                            <div className="absolute top-1 left-1 px-2 py-0.5 bg-red-500 rounded text-white text-xs">
                              Failed
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removePhoto(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={14} className="text-white" />
                          </button>
                          <p className="text-xs text-[#8fa3b8] truncate mt-1">{photo.name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 9. Additional Notes */}
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#F7931E] border-b border-[#F7931E]/30 pb-2">
                    9. Additional Notes
                  </h3>
                  <div>
                    <label className="block text-white font-medium mb-2">Anything else we should know about your project?</label>
                    <textarea
                      name="additional_notes"
                      value={formData.additional_notes}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Share any additional details, special requirements, or questions..."
                      className="form-input rounded-lg resize-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-[#F7931E]/30">
                  <button
                    type="submit"
                    disabled={loading || !formData.full_name || !formData.phone || !formData.email}
                    className="btn-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-[#0d1f33] border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Submit Estimate Request
                        <Send size={20} />
                      </span>
                    )}
                  </button>
                  <p className="text-[#8fa3b8] text-sm text-center mt-4">
                    We'll review your request and contact you within 24–48 hours.
                  </p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstimatePage;
